/* eslint-disable */
import React from 'react';

import { isEqual, get, isEmpty } from 'lodash';
import { shape, arrayOf } from 'prop-types';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { RouterPropTypes } from 'propTypes/RouterPropTypes';
import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons/faLockKeyhole';
import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons/faCircleQuestion';

import MenuItem from '@mui/material/MenuItem';

import { Button, Flexbox } from '@schoolinks/ui-components';

import { DEFAULT_EMPLOYER_LOGO, DEFAULT_SCHOOL_LOGO } from 'config_app';
import { ORG } from 'routes/constants';
import withUserTypeAndOrgInfo from 'containers/withUserTypeAndOrgInfo';
import withRouter from 'containers/withRouter';

import {
  MENU_ICON_LINK_MAP,
  NAV_LINK_MAP,
  MENU_ICON_MAP_ENH,
} from 'components/Common/LayoutV2/constants';

import { getRelevantNavItems } from 'components/Common/LayoutV2/utils';
import useModal from 'hooks/useModal';

import BetaChip from 'components/Common/Chips/Beta';
import Text from 'components/Common/Text';
import Tooltip from 'components/Common/Tooltip';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import Icon from 'components/Common/Layout/components/SideNavIcon';
import Menu from 'components/Common/Layout/components/SideNavMenu';
import SideNavWrapper from 'components/Common/Layout/components/SideNavWrapper';
import { ARROW_KEYS_NAVIGATION_ITEM_CLASS } from 'components/Common/ArrowKeysNavigation';

import s from './DesktopSideNav.css';
import { ensureObject } from 'utils/arrays';

const NavOptionText = ({ text, isEnabled, tooltip, upsellModalData, beta }) => {
  const { showModal, hideModal } = useModal();

  const textEl = (
    <Text variant="titleMd500" color={isEnabled ? 'font' : 'gray'}>
      {text}
    </Text>
  );

  if (isEnabled) {
    return (
      <>
        {beta ? (
          <>
            {textEl} <BetaChip className={s.betaChip} />
          </>
        ) : (
          textEl
        )}
      </>
    );
  }

  const content = (
    <div className={s.lockedOption}>
      <FontAwesomeIcon icon={faLockKeyhole} />
      {textEl}
    </div>
  );

  return (
    <Flexbox justifyContent="space-between" width="100%">
      {!upsellModalData ? (
        <Tooltip placement="left" title={tooltip}>
          {content}
        </Tooltip>
      ) : (
        content
      )}
      {upsellModalData && (
        <Button
          variant="text"
          hasIconRight
          size="small"
          icon={<FontAwesomeIcon icon={faCircleQuestion} />}
          color="blue"
          label="Learn more"
          onClick={() => {
            showModal('FEATURE_UPSELL_MODAL', {
              data: upsellModalData,
              onClose: hideModal,
            });
          }}
        />
      )}
    </Flexbox>
  );
};

@withRouter
@withUserTypeAndOrgInfo
@connect(
  ({ user: { layout = {}, type = [], currentUserMode } = {} }) => ({
    sideNav: getRelevantNavItems(layout, type, 'sideNav', currentUserMode),
  }),
  null,
)
export default class DesktopSideNav extends React.Component {
  shouldComponentUpdate(nextProps, nextStates) {
    return !isEqual(nextProps, this.props) || !isEqual(nextStates, this.state);
  }

  render() {
    const { sideNav = [] } = this.props;
    const {
      router: {
        location: { pathname },
        match,
      },
      user,
      ...restProps
    } = this.props;
    const baseNav = sideNav?.find(el => el.type === 'base');
    const orgNav = sideNav?.find(el => el.type === 'org_member');
    const { selectedOrganizationUuid } = user;

    const MENU_ICON_MAP = MENU_ICON_MAP_ENH(user);

    const NavOptionWrapper = ({ link, isEnabled, children }) => {
      if (!isEnabled) {
        return children;
      }

      if (link.startsWith('/')) {
        return (
          <NavLink to={link} className={ARROW_KEYS_NAVIGATION_ITEM_CLASS}>
            {children}
          </NavLink>
        );
      }

      return (
        <a
          href={link}
          target="_blank"
          rel="noopener noreferrer"
          className={ARROW_KEYS_NAVIGATION_ITEM_CLASS}
        >
          {children}
        </a>
      );
    };

    const NavOption = ({
      text,
      link,
      upsellModalData,
      isEnabled,
      tooltip,
      beta,
    }) => {
      const navContent = (
        <NavOptionText
          text={text}
          isEnabled={isEnabled}
          tooltip={tooltip}
          upsellModalData={upsellModalData}
          beta={beta}
        />
      );

      return (
        <div
          // cspell:disable-next-line
          role="listitem"
          className={s.option}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.target?.querySelector('a')?.click();
            }
          }}
        >
          {!isEnabled && upsellModalData ? (
            navContent
          ) : (
            <NavOptionWrapper link={link} isEnabled={isEnabled}>
              {navContent}
            </NavOptionWrapper>
          )}
        </div>
      );
    };

    return (
      <SideNavWrapper {...restProps}>
        {orgNav &&
          orgNav.items?.map(menu => {
            const { organizationUuid, organizationType } = menu;
            const fallbackLogo =
              organizationType === 'Employer'
                ? DEFAULT_EMPLOYER_LOGO
                : DEFAULT_SCHOOL_LOGO;
            const isMenuActive = selectedOrganizationUuid === organizationUuid;
            return (
              <Icon
                isActive={isMenuActive}
                key={menu.key}
                title={menu.organizationName}
                data-side-nav-key={menu.key}
                exact
                menuKey={menu.key}
                menu={
                  <Menu>
                    <MenuItem className={s.option}>
                      <Text variant="label">{menu.organizationName}:</Text>
                    </MenuItem>
                    {menu.menuOptions.map(option => {
                      const link = `${ORG}/${organizationUuid}${get(
                        NAV_LINK_MAP,
                        option.key,
                      )}`;
                      return (
                        <NavOption
                          key={option.key}
                          data-side-nav-key={option.key}
                          text={option.name}
                          link={link}
                          isEnabled={option.isEnabled}
                          tooltip={option.tooltip}
                          beta={option.beta}
                        />
                      );
                    })}
                  </Menu>
                }
              >
                <div className={s.iconColumn}>
                  <div
                    style={{
                      backgroundImage: menu.img
                        ? `url(${menu.img})`
                        : `url(${fallbackLogo})`,
                    }}
                    className={s.orgImg}
                  />
                </div>
              </Icon>
            );
          })}
        {orgNav && baseNav && <div className={s.divider} />}
        {baseNav &&
          baseNav.items?.map(menu => {
            const menuContext = get(MENU_ICON_MAP, menu.key);
            const MenuIcon = menuContext?.icon;

            const isMenuActive =
              menuContext?.routes?.length &&
              menuContext?.routes.some(el => pathname.startsWith(el));

            const isMenuActiveSpecialCondition = get(
              menuContext,
              'activeCondition',
              () => null,
            )(match, pathname, isMenuActive);

            if (!MenuIcon) {
              console.warn(
                `${menu.key} does not have mapping to any ICON in MENU_ICON_MAP, please add one in order for it to show up on the side nav`,
              );
              return null;
            }
            const menuIconLink = get(MENU_ICON_LINK_MAP, menu.key);
            return (
              <Icon
                isActive={
                  isMenuActiveSpecialCondition !== null
                    ? isMenuActiveSpecialCondition
                    : isMenuActive
                }
                activeClassname={s[menuContext.color]}
                key={menu.key}
                title={menu.name}
                data-side-nav-key={menu.key}
                exact
                linkTo={menuIconLink}
                menuKey={menu.key}
                menu={
                  <Menu>
                    {menu.menuOptions.map(option => {
                      let link = get(NAV_LINK_MAP, option.key);
                      if (option.kriUuids) {
                        link =
                          user.isStudent || !option.hasIndividualKri
                            ? `/key-readiness-indicators/tree/${option.kriUuids[0]}`
                            : `/key-readiness-indicators/${option.kriUuids[0]}`;
                      }

                      const { upsellModalData = null } = ensureObject(option);

                      if (isEmpty(link)) {
                        console.warn(
                          `${option.key} does not have mapping to any link in NAV_LINK_MAP, please add one in order for it to show up on the side nav`,
                        );

                        return null;
                      }

                      return (
                        <NavOption
                          key={option.key}
                          data-side-nav-key={option.key}
                          text={option.name}
                          link={link}
                          isEnabled={option.isEnabled}
                          upsellModalData={upsellModalData}
                          tooltip={option.tooltip}
                          beta={option.beta}
                        />
                      );
                    })}
                  </Menu>
                }
              >
                <div className={s.iconColumn}>
                  <MenuIcon />
                  {menu.name && <div className={s.iconName}>{menu.name}</div>}
                </div>
              </Icon>
            );
          })}
      </SideNavWrapper>
    );
  }
}

DesktopSideNav.propTypes = {
  sideNav: arrayOf(shape({})),
  user: shape({}),
  router: RouterPropTypes,
};

DesktopSideNav.defaultProps = {
  sideNav: [],
  user: {},
  router: undefined,
};
